import React, { useEffect, useState } from 'react';
import documentService from '../../../services/documentService';
import DocumentUploadModal from '../../../components/Documents/DocumentUploadModal';
import CustomSelect from '../../../components/ui/CustomSelect';
import { formatDestinationName } from '../../../utils/destinationUtils';
import { decodeHtmlEntities } from '../../../utils/textUtils';
import formatDate from '../../../utils/formatDate';
import PageHeader from '../../../components/ui/PageHeader';
import BaseModal from '../../../components/ui/BaseModal';
import {
  FileText, Upload, Download, Trash2, Search,
  AlertCircle, RefreshCw, File, Image,
  FileSpreadsheet, FileType, FolderOpen, Loader2
} from 'lucide-react';

const AgentDocumentsPage = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Trip selector state
  const [trips, setTrips] = useState([]);
  const [selectedTripId, setSelectedTripId] = useState(null);
  const [loadingTrips, setLoadingTrips] = useState(false);

  // Load trips
  useEffect(() => {
    const loadTrips = async () => {
      try {
        setLoadingTrips(true);

        // Use mock data in dev mode
        if (import.meta.env.DEV && import.meta.env.VITE_DEV_MOCK_USER) {
          const { getDevMockCrmTripsAndClients } = await import('../../../dev/mockCrmTrips.js');
          const { trips: mockTrips } = getDevMockCrmTripsAndClients();
          setTrips(mockTrips);
          setLoadingTrips(false);
          return;
        }

        const apiUrl = import.meta.env.VITE_API_URL;
        if (!apiUrl) throw new Error('VITE_API_URL is not configured');
        const response = await fetch(`${apiUrl}/api/trips`, { credentials: 'include' });
        if (!response.ok) throw new Error('Failed to fetch trips');
        const tripsData = await response.json();
        const list = Array.isArray(tripsData) ? tripsData : Array.isArray(tripsData?.data) ? tripsData.data : [];
        setTrips(list);
      } catch {
        setError('Failed to load trips');
      } finally {
        setLoadingTrips(false);
      }
    };
    loadTrips();
  }, []);

  // Load categories
  useEffect(() => {
    if (import.meta.env.DEV && import.meta.env.VITE_DEV_MOCK_USER) return;
    const loadCategories = async () => {
      try {
        const response = await documentService.getCategories();
        setCategories(response.data.categories || []);
      } catch { /* silent */ }
    };
    loadCategories();
  }, []);

  // Load documents when trip is selected
  useEffect(() => {
    if (!selectedTripId) {
      setDocuments([]);
      setLoading(false);
      return;
    }
    loadDocuments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTripId, selectedCategory]);

  const loadDocuments = async (refresh = false) => {
    if (!selectedTripId) { setDocuments([]); return; }

    // In dev mock mode, show empty documents (no backend to call)
    if (import.meta.env.DEV && import.meta.env.VITE_DEV_MOCK_USER) {
      setDocuments([]);
      setLoading(false);
      return;
    }

    try {
      if (refresh) setRefreshing(true);
      else setLoading(true);
      setError(null);
      const response = await documentService.getAllDocuments(selectedCategory || null, 'TRIP', selectedTripId);
      setDocuments(response.data.documents || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load documents');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleUploadSuccess = (newDocument) => {
    setDocuments(prev => [newDocument, ...prev]);
    setShowUploadModal(false);
  };

  const handleDownload = async (documentId) => {
    try {
      const response = await documentService.getDownloadUrl(documentId);
      window.open(response.data.downloadUrl, '_blank');
    } catch {
      setError('Failed to download document');
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await documentService.deleteDocument(deleteTarget.id);
      setDocuments(prev => prev.filter(doc => doc.id !== deleteTarget.id));
      setDeleteTarget(null);
    } catch {
      setError('Failed to delete document');
    } finally {
      setDeleting(false);
    }
  };

  const getFileIcon = (mimeType) => {
    if (mimeType?.includes('pdf')) return <FileText className="w-5 h-5 text-red-500" />;
    if (mimeType?.includes('image')) return <Image className="w-5 h-5 text-blue-500" />;
    if (mimeType?.includes('word') || mimeType?.includes('document')) return <FileType className="w-5 h-5 text-blue-600" />;
    if (mimeType?.includes('excel') || mimeType?.includes('spreadsheet')) return <FileSpreadsheet className="w-5 h-5 text-green-500" />;
    return <File className="w-5 h-5 text-gray-500" />;
  };

  const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return '—';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  const filteredDocuments = documents.filter(doc => {
    const q = searchQuery.toLowerCase();
    const matchesSearch = !q ||
      doc.originalName?.toLowerCase().includes(q) ||
      doc.description?.toLowerCase().includes(q) ||
      doc.confirmationNumber?.toLowerCase().includes(q);
    const matchesCategory = !selectedCategory || doc.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const selectedTrip = trips.find(t => t.tripid === selectedTripId);

  return (
    <div className="space-y-5">
      {/* Header */}
      <PageHeader
        breadcrumbs={[
          { label: 'Dashboard', href: '/agent/dashboard' },
          { label: 'Wonder Vault' },
        ]}
        title="Wonder Vault"
        subtitle="Upload and manage documents for your clients' trips"
        actions={
          <>
            <button
              onClick={() => loadDocuments(true)}
              disabled={refreshing || !selectedTripId}
              className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
              type="button"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            <button
              onClick={() => setShowUploadModal(true)}
              disabled={!selectedTripId}
              className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
              type="button"
            >
              <Upload className="w-4 h-4" />
              Upload
            </button>
          </>
        }
      />

      {/* Error */}
      {error && (
        <div className="flex items-center gap-2 p-3 text-sm bg-red-50 border border-red-200 rounded-lg text-red-700">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          <span>{error}</span>
          <button onClick={() => { setError(null); loadDocuments(); }} className="ml-auto text-xs font-medium underline">Retry</button>
        </div>
      )}

      {/* Trip Selector */}
      <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm">
        <label className="block mb-1.5 text-sm font-medium text-gray-700">Select a trip to manage documents</label>
        <CustomSelect
          id="trip-select"
          placeholder="-- Choose a trip --"
          value={selectedTripId || ''}
          onChange={(value) => setSelectedTripId(value || null)}
          loading={loadingTrips}
          disabled={loadingTrips}
          className="max-w-lg"
          options={trips.map((trip) => ({
            value: trip.tripid,
            label: `${decodeHtmlEntities(trip.tripname)}${trip.fullname ? ` — ${trip.fullname}` : ''} (${formatDestinationName(trip.destination)})`,
          }))}
        />
      </div>

      {/* No trip selected */}
      {!selectedTripId && (
        <div className="py-16 text-center bg-white rounded-xl border border-gray-200 shadow-sm">
          <FolderOpen className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <h3 className="text-base font-semibold text-gray-900 mb-1">No trip selected</h3>
          <p className="text-sm text-gray-500 max-w-xs mx-auto">
            Choose a trip above to view, upload, and manage documents like confirmations, receipts, and itineraries.
          </p>
        </div>
      )}

      {/* Trip selected content */}
      {selectedTripId && (
        <>
          {/* Search + filter + count bar */}
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search documents..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full h-10 pl-10 pr-4 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            {categories.length > 0 && (
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="h-10 px-3 pr-8 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none sm:w-44"
              >
                <option value="">All Categories</option>
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.id}>{cat.name}</option>
                ))}
              </select>
            )}
            {!loading && (
              <span className="text-xs text-gray-500 whitespace-nowrap">
                {filteredDocuments.length} document{filteredDocuments.length !== 1 ? 's' : ''}
              </span>
            )}
          </div>

          {/* Loading */}
          {loading && !refreshing && (
            <div className="flex justify-center py-12 bg-white rounded-xl border border-gray-200 shadow-sm">
              <Loader2 className="w-6 h-6 text-blue-600 animate-spin" />
            </div>
          )}

          {/* Empty */}
          {!loading && filteredDocuments.length === 0 && (
            <div className="py-12 text-center bg-white rounded-xl border border-gray-200 shadow-sm">
              <FileText className="w-10 h-10 text-gray-300 mx-auto mb-3" />
              <h3 className="text-sm font-semibold text-gray-900 mb-1">No documents found</h3>
              <p className="text-xs text-gray-500 mb-4">
                {searchQuery || selectedCategory
                  ? 'Try adjusting your search or filter'
                  : 'Upload your first document for this trip'}
              </p>
              {!searchQuery && !selectedCategory && (
                <button
                  onClick={() => setShowUploadModal(true)}
                  className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
                  type="button"
                >
                  <Upload className="w-4 h-4" /> Upload Document
                </button>
              )}
            </div>
          )}

          {/* Document list — card-based for better responsiveness */}
          {!loading && filteredDocuments.length > 0 && (
            <div className="space-y-2">
              {filteredDocuments.map((doc) => {
                const categoryInfo = categories.find(cat => cat.id === doc.category) || { name: doc.category || 'Uncategorized' };
                return (
                  <div key={doc.id} className="flex items-center gap-3 px-4 py-3 bg-white rounded-lg border border-gray-200 shadow-sm hover:border-gray-300 transition-colors">
                    {/* File icon */}
                    <div className="flex-shrink-0">
                      {getFileIcon(doc.mimeType)}
                    </div>

                    {/* File info */}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{doc.originalName}</p>
                      <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-0.5">
                        <span className="text-xs text-gray-500">{formatFileSize(doc.fileSize)}</span>
                        <span className="text-xs text-gray-500">{categoryInfo.name}</span>
                        <span className="text-xs text-gray-400">{formatDate(doc.uploadedAt, { format: 'medium' })}</span>
                        {doc.confirmationNumber && (
                          <span className="text-xs text-gray-500">#{doc.confirmationNumber}</span>
                        )}
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button
                        onClick={() => handleDownload(doc.id)}
                        className="p-2 text-gray-400 rounded-lg hover:text-blue-600 hover:bg-blue-50 transition-colors"
                        title="Download"
                        type="button"
                      >
                        <Download className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setDeleteTarget(doc)}
                        className="p-2 text-gray-400 rounded-lg hover:text-red-600 hover:bg-red-50 transition-colors"
                        title="Delete"
                        type="button"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* Delete Confirmation */}
      <BaseModal isOpen={!!deleteTarget} onClose={() => setDeleteTarget(null)} title="Delete Document" size="sm">
        <BaseModal.Body>
          <p className="text-sm text-gray-600">
            Are you sure you want to delete <strong>{deleteTarget?.originalName}</strong>? This cannot be undone.
          </p>
        </BaseModal.Body>
        <BaseModal.Footer>
          <button
            onClick={() => setDeleteTarget(null)}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
            type="button"
          >
            Cancel
          </button>
          <button
            onClick={handleDelete}
            disabled={deleting}
            className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
            type="button"
          >
            {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
            {deleting ? 'Deleting...' : 'Delete'}
          </button>
        </BaseModal.Footer>
      </BaseModal>

      {/* Upload Modal */}
      <DocumentUploadModal
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        onUploadSuccess={handleUploadSuccess}
        tripId={selectedTripId || undefined}
        clientId={undefined}
        clientName={decodeHtmlEntities(selectedTrip?.tripname) || 'trip'}
      />
    </div>
  );
};

export default AgentDocumentsPage;
